import { Box } from '@hivebrite/design-system/Box';
import {
  ContentList,
  CONTENT_TYPES,
} from '@hivebrite/design-system/ContentList';
import { Divider } from '@hivebrite/design-system/Divider';
import { CATEGORIES } from '@hivebrite/design-system/FontAwesomeIcon';
import { Pill, PILL_STATUS } from '@hivebrite/design-system/Pill';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { RecommendedContentCardSkeleton } from 'hb-react/shared/components/ContentRecommendation/components/RecommendedContentCardSkeleton';
import { useGetRecommendedContents } from 'hb-react/shared/components/ContentRecommendation/hooks/useGetRecommendedContents';
import { type EntityType } from 'hb-react/shared/utils/entitiesContentData/types';

export const displayFields = {
  contentType: true,
  [CONTENT_TYPES.CUSTOM_PAGE]: {
    content: true,
    group: true,
  },
  [CONTENT_TYPES.EVENT]: {
    date: true,
    group: true,
  },
  [CONTENT_TYPES.FILE]: {
    description: true,
    group: true,
    user: true,
  },
  [CONTENT_TYPES.FOLDER]: {
    description: true,
    group: true,
    user: true,
  },
  [CONTENT_TYPES.FORUM]: {
    author: true,
    content: true,
    group: true,
  },
  [CONTENT_TYPES.GROUP]: {
    avatars: false,
    location: true,
    members: true,
  },
  [CONTENT_TYPES.NEWS]: {
    content: true,
    date: true,
    group: true,
  },
  [CONTENT_TYPES.OPPORTUNITY]: {
    company: true,
    group: true,
  },
  [CONTENT_TYPES.ORGANIZATION]: {
    description: true,
    group: true,
  },
  [CONTENT_TYPES.POST]: {
    attachments: true,
    group: true,
    publishedAt: true,
    trip: true,
  },
  [CONTENT_TYPES.PROJECT]: {
    description: true,
    location: true,
    group: true,
  },
};

interface ContentRecommendationProps {
  contentId: number;
  contentType: EntityType;
  max?: number;
  recommendedContentTypes?: Array<EntityType>;
}

export const ContentRecommendation = ({
  contentId,
  contentType,
  max = 3,
  recommendedContentTypes,
}: ContentRecommendationProps) => {
  const { topicId: groupId } = useTopicContext();

  const { data: contentItems, isLoading } = useGetRecommendedContents({
    contentId,
    contentType,
    groupId,
    recommendedContentTypes,
  });

  if (!isLoading && contentItems.length === 0) {
    return null;
  }

  return (
    <Box mb={6} flexDirection="column">
      <Divider mb={8} />
      {isLoading && contentItems.length === 0 ? (
        Array.from({ length: max }).map((_, index) => (
          <RecommendedContentCardSkeleton key={index} />
        ))
      ) : (
        <>
          <Box
            flexDirection="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box
              as="h2"
              font="h3-semibold"
              justifyContent="space-between"
              pb={4}
            >
              {s__('ContentRecommendation|You may be interested in')}
            </Box>
            <Box flexShrink={0}>
              <Pill
                iconName="sparkles"
                iconType={CATEGORIES.REGULAR}
                status={PILL_STATUS.HIGHLIGHT}
              >
                {__('Beta')}
              </Pill>
            </Box>
          </Box>
          <ContentList
            data={contentItems}
            displayFields={displayFields}
            isCardClickable
            isLoading={isLoading}
            max={max}
            variant="bordered"
          />
        </>
      )}
    </Box>
  );
};
